// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { Store } from 'antd/lib/form/interface';
import { Form, FormInstance } from 'antd';
import { GaugeWidgetType, WidgetOutput, FilterInput, ObjectTypeV1 } from 'thehive-sdk';
import DividerBasics from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerBasics';
import DividerFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerFilter';
import ProvidedWidgetFormFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormFilter/ProvidedWidgetFormFilter';
import TitleField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/TitleField';
import EntityField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/EntityField';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { entitiesToExclude } from '@Constants/dashboard';

import './GaugeForm.scss';

const checkGaugeValue = (value: number, greaterValue: number): boolean => value > greaterValue;

export interface GaugeValues extends Store {
    title: string;
    entity: ObjectTypeV1;
    filters: FilterInput[];
    periodDateField?: string;
    safe: number;
    warning: number;
    critical: number;
}

interface GaugeFormProps {
    onFinish: (values: GaugeValues) => void;
    defaultValues?: WidgetOutput & { options: GaugeWidgetType };
    form: FormInstance;
    isValid?: boolean;
    onCancel: () => void;
    onChange: () => void;
}

export const GaugeForm = ({
    onFinish,
    defaultValues,
    form,
    onCancel,
    onChange,
}: GaugeFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const entity = Form.useWatch(['entity'], form);

    return (
        <THForm<GaugeValues>
            name="gauge-form"
            form={form}
            onFinish={onFinish}
            onValuesChange={onChange}
            initialValues={{
                title: defaultValues?.options.title,
                entity: defaultValues?.options.entity,
                filters: defaultValues?.options.filters,
                periodDateField: defaultValues?.options.periodDateField || '_createdAt',
                safe: defaultValues?.options.safe,
                warning: defaultValues?.options.warning,
                critical: defaultValues?.options.critical,
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={false} footerText={i('general.confirm')} />}
            >
                <DividerBasics />
                <TitleField />
                <EntityField form={form} showPeriodDate entitiesToExclude={entitiesToExclude} />

                <div className="gauge-form__counter">
                    <THForm.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => (
                            <THForm.Item
                                name="safe"
                                label={i('app.dashboard.widget.create.form.min.label')}
                                rules={[
                                    {
                                        required: true,
                                        validator: (_, value) => {
                                            if (checkGaugeValue(value, getFieldValue('warning'))) {
                                                return Promise.reject(
                                                    i('app.dashboard.widget.create.form.gauge.required'),
                                                );
                                            }

                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <THInputNumber placeholder={i('app.dashboard.widget.create.form.min.placeholder')} />
                            </THForm.Item>
                        )}
                    </THForm.Item>

                    <THForm.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => (
                            <THForm.Item
                                name="warning"
                                label={i('app.dashboard.widget.create.form.medium.label')}
                                rules={[
                                    {
                                        required: true,
                                        validator: (_, value) => {
                                            if (checkGaugeValue(value, getFieldValue('critical'))) {
                                                return Promise.reject(
                                                    i('app.dashboard.widget.create.form.gauge.required'),
                                                );
                                            }

                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <THInputNumber placeholder={i('app.dashboard.widget.create.form.medium.placeholder')} />
                            </THForm.Item>
                        )}
                    </THForm.Item>

                    <THForm.Item
                        name="critical"
                        label={i('app.dashboard.widget.create.form.max.label')}
                        rules={[{ required: true }]}
                    >
                        <THInputNumber placeholder={i('app.dashboard.widget.create.form.max.placeholder')} />
                    </THForm.Item>
                </div>

                <DividerFilter />
                <THForm.Item name="filters">
                    <ProvidedWidgetFormFilter entity={entity} />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
